import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { CSSProperties, ReactNode } from 'react'
import { PiArrowRight, PiCaretRight, PiHouse, PiInfo, PiMagnifyingGlass, PiQrCode, PiUser } from 'react-icons/pi'
import { APP_NAME } from '@/constants/app.constant'
import type { PublicAssetFrontendApi$AssetComponentDto } from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'
import CallTarget from './CallTarget'
import { componentIcon } from './componentIcon'
import { mix, normaliseHex, readableAccentOn, readableInk } from './theme'
import { visitedLink } from './visited'
import type { VisitedEntry } from './useHomeModel'
// Loaded by the shared kit so the design's CSS is present whether the visitor
// arrives at the home or directly at an asset view (a scanned link).
import './redesigns.css'

/**
 * The tile-world primitives, driven entirely by theme CSS variables so a colour
 * trial is a token swap, not a rewrite. Every variant shares these, which keeps
 * the forward structure and the tile rules identical across palettes and faces.
 */
export type ForwardTheme = {
  ground: string
  onGround: string
  surface: string
  onSurface: string
  neutral: string
  onNeutral: string
  accent: string
  onAccent: string
  muted: string
  line: string
}

/** The one repeated action cue: a chevron on every tile that opens something. */
function ActionChevron({ className = 'h-5 w-5 opacity-60' }: { className?: string }) {
  return <PiCaretRight aria-hidden className={'shrink-0 rtl:-scale-x-100 ' + className} />
}

export function tileVars(theme: ForwardTheme): CSSProperties {
  return {
    '--rda-ground': theme.ground,
    '--rda-on-ground': theme.onGround,
    '--rda-surface': theme.surface,
    '--rda-on-surface': theme.onSurface,
    '--rda-neutral': theme.neutral,
    '--rda-on-neutral': theme.onNeutral,
    '--rda-accent': theme.accent,
    '--rda-on-accent': theme.onAccent,
    // A version of the accent legible as text/indicator on the light surface.
    '--rda-accent-text': readableAccentOn(theme.accent, theme.surface),
    '--rda-muted': theme.muted,
    '--rda-line': theme.line,
    // Quiet structure for grouped surfaces: hairlines and card borders, and a
    // faint accent wash / tint for the support cards and their arrow disc.
    '--rda-hairline': 'rgba(16, 18, 21, 0.09)',
    '--rda-accent-wash': `color-mix(in srgb, ${theme.accent} 6%, ${theme.surface})`,
    '--rda-accent-tint': `color-mix(in srgb, ${theme.accent} 14%, ${theme.surface})`,
    // The press-flood in redesigns.css reads these two.
    '--rda-ink': theme.onAccent,
  } as CSSProperties
}

/**
 * The neutral half of the palette is fixed; only the accent comes from the
 * organisation, and its readable foreground is derived so any brand colour
 * works.
 */
const NEUTRALS: Omit<ForwardTheme, 'accent' | 'onAccent'> = {
  ground: '#F7F8F6',
  onGround: '#101215',
  surface: '#FFFFFF',
  onSurface: '#101215',
  neutral: '#E9EAE6',
  onNeutral: '#101215',
  muted: '#5B6169',
  // 3:1 against the white field it borders, per WCAG 1.4.11.
  line: 'rgba(16, 18, 21, 0.5)',
}

/**
 * Re-points the accent variables at a component's own tint (set per component
 * in the admin), so its card, icon and press-flood use that colour instead of
 * the brand colour. Null leaves the brand colour in place.
 */
export function tintVars(tintHex: string | null | undefined): CSSProperties | undefined {
  if (!tintHex) return undefined
  const tint = normaliseHex(tintHex)
  const surface = NEUTRALS.surface
  return {
    '--rda-accent': tint,
    '--rda-ink': readableInk(tint),
    '--rda-accent-text': readableAccentOn(tint, surface),
    '--rda-accent-wash': mix(surface, tint, 0.09),
    '--rda-accent-tint': mix(surface, tint, 0.2),
  } as CSSProperties
}

export function forwardTheme(accent: string): ForwardTheme {
  return { ...NEUTRALS, accent, onAccent: readableInk(accent) }
}

/**
 * The shared page shell for the asset frontend. It owns the ground, the font,
 * the theme variables and the 16px-gutter column, so the home and the asset
 * view cannot drift apart.
 */
export function AssetShell({
  accent,
  nav,
  children,
}: {
  accent: string
  nav?: ReactNode
  children: ReactNode
}) {
  return (
    <div
      className="rda-font-schibsted min-h-screen w-full bg-[var(--rda-ground)] pb-20 text-[var(--rda-on-ground)]"
      data-seed="d920a20a"
      style={tileVars(forwardTheme(accent))}
    >
      <div className="mx-auto w-full max-w-xl">{children}</div>
      {nav}
    </div>
  )
}

/**
 * Org mark and the "what is this?" explainer. The logo is given the room the
 * brand needs; the explainer is a ghost icon button so it stops competing with
 * it, and the bar is tight so the top of the page is not mostly empty.
 */
export function PreviewHeader({
  logoUrl,
  infoLabel,
  onInfo,
}: {
  logoUrl: string
  infoLabel?: string
  onInfo?: () => void
}) {
  return (
    <header className="flex items-center justify-between gap-3 px-4 pb-3 pt-3">
      <img
        src={logoUrl}
        alt={`${APP_NAME} logo`}
        className="h-11 w-auto max-w-[72%] object-contain"
      />
      {onInfo ? (
        <button
          type="button"
          aria-label={infoLabel}
          title={infoLabel}
          className="rda-tile flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--rda-muted)]"
          onClick={onInfo}
        >
          <PiInfo className="text-lg" />
        </button>
      ) : null}
    </header>
  )
}

/**
 * The Scan key that sits beside a search field, with a small "or" before it:
 * visitors otherwise read the key as the field's submit button.
 */
export function OrScan({ onScan }: { onScan: () => void }) {
  const { t } = useTranslation()
  return (
    <>
      <span
        aria-hidden
        className="flex shrink-0 items-center text-[13px] font-medium lowercase text-[var(--rda-muted)]"
      >
        {t('assetSelection.or')}
      </span>
      <button
        type="button"
        className="rda-tile flex shrink-0 items-center gap-2 rounded-lg sm:rounded-xl bg-[var(--rda-accent)] px-3 text-[var(--rda-on-accent)] sm:px-4"
        onClick={onScan}
      >
        <PiQrCode aria-hidden className="h-5 w-5" />
        <span className="text-[13px] font-semibold tracking-[-0.01em] sm:text-sm">{t('bottom_menu.scan')}</span>
      </button>
    </>
  )
}

/**
 * The home's serial search, in the same field as the asset page's search: a
 * rounded bordered input led by the magnifier, then "or" and the Scan key.
 */
export function SerialSearch({
  lookup,
  placeholder,
  ariaLabel,
  onScan,
}: {
  lookup: { query: string; setQuery: (v: string) => void; isSearching: boolean }
  placeholder: string
  ariaLabel: string
  onScan: () => void
}) {
  return (
    <div className="flex items-stretch gap-2">
      <label className="flex min-w-0 flex-1 items-center gap-2 rounded-lg sm:rounded-xl border border-[color:var(--rda-line)] bg-[var(--rda-surface)] px-3 text-[var(--rda-on-surface)] focus-within:border-[color:var(--rda-accent-text)] focus-within:ring-1 focus-within:ring-[color:var(--rda-accent-text)]">
        <PiMagnifyingGlass aria-hidden className="h-5 w-5 shrink-0 text-[var(--rda-muted)]" />
        <input
          value={lookup.query}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="w-full min-w-0 bg-transparent py-2.5 text-base sm:py-3 outline-none placeholder:text-[color:var(--rda-muted)]"
          onChange={(e) => lookup.setQuery(e.target.value)}
        />
        {lookup.isSearching ? (
          <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-50" />
        ) : null}
      </label>
      <OrScan onScan={onScan} />
    </div>
  )
}

/** The small spaced heading above a list ("Documentation", "Recently visited"). */
export function SectionHeading({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2
      id={id}
      className="text-[11px] font-bold uppercase tracking-[0.2em] sm:text-[12px] sm:tracking-[0.22em] text-[var(--rda-on-ground)]"
    >
      {children}
    </h2>
  )
}

/** A dense resource row: icon, name, chevron in one scannable line. */
export function ResourceRow({
  component,
  tone = 'surface',
  subtitle,
  size = 'md',
  grouped = false,
  tinted = false,
  instantPhone = null,
  onClick,
}: {
  component: PublicAssetFrontendApi$AssetComponentDto
  tone?: 'surface' | 'accent'
  subtitle?: string
  size?: 'md' | 'lg'
  /** Inside a `RowGroup`: the group draws the field and the dividers, so no base tick. */
  grouped?: boolean
  /** Colour the icon with the component's tint (see `tintVars`). */
  tinted?: boolean
  instantPhone?: string | null
  onClick: () => void
}) {
  const big = size === 'lg'
  return (
    <CallTarget
      className={
        'rda-tile flex w-full items-center gap-3 text-start ' +
        (grouped ? '' : 'rda-edge ') +
        (big ? 'px-4 py-2.5 sm:py-4 ' : 'px-4 py-2.5 sm:py-3 ') +
        (tone === 'accent'
          ? 'bg-[var(--rda-accent)] text-[var(--rda-on-accent)]'
          : 'bg-[var(--rda-surface)] text-[var(--rda-on-surface)]')
      }
      instantPhone={instantPhone}
      style={tinted ? tintVars(component.tintHex) : undefined}
      onClick={onClick}
    >
      <span
        aria-hidden
        className={
          (big ? 'text-[22px] sm:text-[28px]' : 'text-lg sm:text-xl') +
          (tinted && component.tintHex ? ' text-[var(--rda-accent-text)]' : '')
        }
      >
        {componentIcon(component.key)}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={
            'block font-medium leading-tight tracking-[-0.01em] ' +
            (big ? 'text-sm sm:text-base' : 'text-[13px] sm:text-sm')
          }
        >
          {component.name}
        </span>
        {subtitle ? (
          <span
            className={
              'mt-0.5 block ' +
              (grouped ? 'rda-tile-muted line-clamp-2 text-[var(--rda-muted)] ' : 'truncate opacity-80 ') +
              (big ? 'text-sm' : 'text-xs')
            }
          >
            {subtitle}
          </span>
        ) : null}
      </span>
      <ActionChevron className={big ? 'h-5 w-5 opacity-60' : 'h-4 w-4 opacity-60'} />
    </CallTarget>
  )
}

/**
 * A rounded list that holds `grouped` resource rows: one field, hairline
 * dividers between rows, and the page gutter on both sides.
 */
export function RowGroup({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col divide-y divide-[color:var(--rda-hairline)] overflow-hidden rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)]">
      {children}
    </div>
  )
}

/**
 * A large support action tile, after the reference mockup: a big icon, the
 * name, an optional description, and a circular action arrow anchored to the
 * bottom.
 */
export function SupportCard({
  component,
  subtitle,
  instantPhone = null,
  onClick,
}: {
  component: PublicAssetFrontendApi$AssetComponentDto
  subtitle?: string
  instantPhone?: string | null
  onClick: () => void
}) {
  return (
    <CallTarget
      style={tintVars(component.tintHex)}
      className="rda-tile flex min-h-[148px] min-w-0 flex-col items-start gap-2 sm:min-h-[200px] sm:gap-3 rounded-[10px] sm:rounded-2xl bg-[var(--rda-accent-wash)] p-3 text-start sm:p-4 text-[var(--rda-on-surface)]"
      instantPhone={instantPhone}
      onClick={onClick}
    >
      <span aria-hidden className="text-[22px] leading-none text-[var(--rda-accent-text)] sm:text-[32px]">
        {componentIcon(component.key)}
      </span>
      <span className="block w-full min-w-0 break-words max-[419px]:hyphens-auto">
        <span className="block text-[13px] font-semibold leading-tight sm:text-[17px] tracking-[-0.02em]">
          {component.name}
        </span>
        {subtitle ? (
          <span className="rda-tile-muted mt-1 block text-xs leading-snug sm:mt-1.5 sm:text-sm text-[var(--rda-muted)]">
            {subtitle}
          </span>
        ) : null}
      </span>
      <span
        aria-hidden
        className="mt-auto flex h-7 w-7 items-center justify-center rounded-full sm:h-10 sm:w-10 bg-[var(--rda-accent-tint)] text-[var(--rda-accent-text)]"
      >
        <PiArrowRight className="text-sm sm:text-lg" />
      </span>
    </CallTarget>
  )
}

/**
 * Recently visited assets as a horizontal row of cards, in the page's card
 * language; the serial stays monospace because it is a measurement.
 */
export function RecentStrip({ recent }: { recent: VisitedEntry[] }) {
  const { t } = useTranslation()
  return (
    <section aria-labelledby="recent-heading" className="flex flex-col gap-2">
      <SectionHeading id="recent-heading">{t('home.assets.recentlyVisited')}</SectionHeading>
      <div className="rda-scroller -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scroll-padding-inline:1rem]">
        {recent.map((entry, i) => {
          const link = visitedLink(entry)
          if (link == null) return null
          return (
            <Link
              key={link.to + i}
              to={link.to}
              className="rda-tile flex min-h-[76px] w-[156px] sm:min-h-[84px] sm:w-[168px] shrink-0 flex-col justify-between gap-2 rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] px-4 py-3 text-[var(--rda-on-surface)]"
            >
              <span className="flex w-full items-start justify-between gap-2">
                <span className="rda-tile-muted font-mono text-[12px] tracking-wider text-[var(--rda-muted)]">
                  {link.secondary}
                </span>
                <ActionChevron className="h-4 w-4 opacity-50" />
              </span>
              <span className="text-sm font-semibold leading-tight tracking-[-0.01em] sm:text-base">
                {link.primary}
              </span>
            </Link>
          )
        })}
      </div>
    </section>
  )
}

export type PreviewNavTab = 'home' | 'scan' | 'account'

/**
 * The bottom nav: a surface strip spanning the same 16px-gutter column as the
 * page content, with a rounded accent key for the active destination.
 *
 * `active` is null on an asset page: that page is not Home, and none of the
 * three tabs is where the visitor is.
 */
export function PreviewNav({
  active,
  onHome,
  onScan,
  onAccount,
}: {
  active: PreviewNavTab | null
  onHome: () => void
  onScan: () => void
  onAccount: () => void
}) {
  const { t } = useTranslation()
  return (
    <nav className="fixed bottom-0 left-0 z-40 w-full border-t border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto grid h-14 max-w-xl grid-cols-3 gap-2 px-4 py-1.5 sm:h-16">
        <NavTile
          active={active === 'home'}
          icon={<PiHouse />}
          label={t('bottom_menu.home')}
          onClick={onHome}
        />
        <NavTile
          active={active === 'scan'}
          icon={<PiQrCode />}
          label={t('bottom_menu.scan')}
          onClick={onScan}
        />
        <NavTile
          active={active === 'account'}
          icon={<PiUser />}
          label={t('bottom_menu.account')}
          onClick={onAccount}
        />
      </div>
    </nav>
  )
}

function NavTile({
  label,
  icon,
  active,
  onClick,
}: {
  label: string
  icon: ReactNode
  active?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      className={
        'rda-tile flex flex-col items-center justify-center gap-0.5 px-2 text-center ' +
        'rounded-lg sm:rounded-xl ' +
        (active
          ? 'bg-[var(--rda-accent)] text-[var(--rda-on-accent)]'
          : 'bg-[var(--rda-surface)] text-[var(--rda-on-surface)]')
      }
      onClick={onClick}
    >
      <span aria-hidden className="text-lg">
        {icon}
      </span>
      <span className="text-[11px] font-bold uppercase leading-none tracking-[0.14em]">
        {label}
      </span>
    </button>
  )
}

/** Loading placeholder in the page's shape: three boxes, then a list. */
export function CardsSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="min-h-[148px] animate-pulse sm:min-h-[200px] rounded-[10px] sm:rounded-2xl bg-[var(--rda-accent-wash)]" />
        ))}
      </div>
      <div className="flex flex-col divide-y divide-[color:var(--rda-hairline)] overflow-hidden rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)]">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-4">
            <span className="h-7 w-7 animate-pulse rounded-md bg-black/10" />
            <span className="h-3 w-2/5 animate-pulse rounded bg-black/10" />
          </div>
        ))}
      </div>
    </div>
  )
}
